import { Logger } from 'loglevel';
import { EdiAdminBaseService } from './EdiAdminBaseService';
import {
  oauthClientUserListSchema,
  oauthClientUserSchema,
  type OAuthClientUser,
} from '../types/schemas/clientUser.schema';

export type { OAuthClientUser };

export class EdiAdminClientUsersService extends EdiAdminBaseService {
  constructor(
    private readonly getAccessToken: () => Promise<string>,
    logger: Logger,
  ) {
    super(logger);
  }
  async fetchGetClientUsers(): Promise<OAuthClientUser[]> {
    const accessToken = await this.getAccessToken();
    const apiFetchUrl = `${this.apiBaseUrl}/OAuthClient/ClientUsers/GetClientUsers`;

    this.logger.info('Fetching client users:', {
      url: apiFetchUrl,
    });

    const response = await fetch(apiFetchUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    this.logger.info('📥 Response fetch:', {
      url: response.url,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      await this.handleEdiApiServiceResponseError(
        response,
        'Erreur lors de la récupération des utilisateurs client',
      );
    }

    await this.checkEdiApiServiceResponseContentType(response);

    const data: unknown = await response.json();

    return this.parseResponse(
      oauthClientUserListSchema,
      Array.isArray(data) ? data : [],
      'GetClientUsers',
    );
  }

  async fetchCreateClientUsers(user: OAuthClientUser): Promise<OAuthClientUser> {
    const accessToken = await this.getAccessToken();
    const apiFetchUrl = `${this.apiBaseUrl}/OAuthClient/ClientUsers/CreateClientUser`;
    const body = JSON.stringify(user);

    this.logger.info('Creating client user:', { url: apiFetchUrl });

    const response = await fetch(apiFetchUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body,
    });

    this.logger.info('📥 Response fetch:', {
      url: response.url,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      await this.handleEdiApiServiceResponseError(
        response,
        "Erreur lors de la création de l'utilisateur client",
      );
    }

    await this.checkEdiApiServiceResponseContentType(response);

    const data: unknown = await response.json();

    return this.parseResponse(oauthClientUserSchema, data, 'CreateClientUser');
  }

  async fetchUpdateClientUsers(user: OAuthClientUser): Promise<OAuthClientUser> {
    const accessToken = await this.getAccessToken();
    const apiFetchUrl = `${this.apiBaseUrl}/OAuthClient/ClientUsers/UpdateClientUser`;
    const body = JSON.stringify(user);

    this.logger.info('Updating client user:', { url: apiFetchUrl });

    const response = await fetch(apiFetchUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body,
    });

    this.logger.info('📥 Response fetch:', {
      url: response.url,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      await this.handleEdiApiServiceResponseError(
        response,
        "Erreur lors de la mise à jour de l'utilisateur client",
      );
    }

    await this.checkEdiApiServiceResponseContentType(response);

    const data: unknown = await response.json();

    return this.parseResponse(oauthClientUserSchema, data, 'UpdateClientUser');
  }

  async fetchResetClientUser(instanceId: string): Promise<OAuthClientUser> {
    const accessToken = await this.getAccessToken();
    const apiFetchUrl = `${this.apiBaseUrl}/OAuthClient/ClientUsers/ResetClientUser?instanceId=${instanceId}`;

    this.logger.info('Deleting client user:', { url: apiFetchUrl, instanceId });

    const response = await fetch(apiFetchUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    this.logger.info('📥 Response fetch:', {
      url: response.url,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      await this.handleEdiApiServiceResponseError(
        response,
        'Erreur lors de la réinitialisation de l’utilisateur client',
      );
    }

    await this.checkEdiApiServiceResponseContentType(response);

    const data: unknown = await response.json();

    return this.parseResponse(oauthClientUserSchema, data, 'ResetClientUser');
  }
}
